import React, { useEffect, useState } from 'react';
import { Users, Star, TrendingUp, Clock } from 'lucide-react';
import { analyticsAPI } from '../../services/api';
import { format } from 'date-fns';

const StaffDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await analyticsAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 pt-12 lg:pt-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
        <div className="skeleton h-96 rounded-2xl" />
      </div>
    );
  }

  const statCards = [
    {
      icon: Users,
      label: "Today's Visits",
      value: stats?.today?.customer_visits || 0,
      color: 'bg-[#4A154B]',
    },
    {
      icon: Star,
      label: 'Points Awarded',
      value: stats?.today?.points_awarded || 0,
      color: 'bg-[#C9A689]',
    },
    {
      icon: TrendingUp,
      label: "Today's Revenue",
      value: `$${(stats?.today?.revenue || 0).toFixed(0)}`,
      color: 'bg-[#8FBC8F]',
    },
    {
      icon: Clock,
      label: 'Total Customers',
      value: stats?.total_customers || 0,
      color: 'bg-[#2C2C2C]',
    },
  ];

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B]">Staff Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's your daily overview.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="glass rounded-2xl p-5 card-hover" data-testid={`stat-card-${index}`}>
            <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
            <p className="font-serif text-2xl text-[#4A154B]">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Recent Transactions */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-serif text-xl text-[#4A154B] mb-4">Recent Transactions</h3>
        
        {stats?.recent_transactions?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between py-3 border-b border-[#F5E6D3] last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    transaction.type === 'earned' ? 'bg-[#8FBC8F]/20' : 'bg-[#C9A689]/20'
                  }`}>
                    <Star className={`w-5 h-5 ${
                      transaction.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C9A689]'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-[#4A154B]">
                      {transaction.service_name || transaction.reward_name || 'Transaction'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(transaction.created_at), 'h:mm a')}
                    </p>
                  </div>
                </div>
                <span className={`font-semibold ${
                  transaction.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C74545]'
                }`}>
                  {transaction.type === 'earned' ? '+' : ''}{transaction.points} pts
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No transactions today</p>
        )}
      </div>
    </div>
  );
};

export default StaffDashboard;
